import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Spin, message, Button } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { theme } from '../../theme';

interface EmisionClasificacionData {
  id: number;
  fecha: string;
  nro_planilla: number;
  alta: number;
  hvi: string;
  observaciones: string;
  IA: string;
  origen: string;
  cliente: string;
  empresa: {
    razon: string;
    direccion: string;
    localidad: string;
    provincia: string;
  };
  detalles: {
    nro_lote: number;
    nroFardo: string;
    calidad: string;
    alta: number;
    hvi: {
      hvi: number;
      SCI: number;
      MST: number;
      UHML: number;
      ML: number;
      UI: number;
      STR: number;
      ELG: number;
      MIC: number;
      AMT: number;
      RD: number;
      B: number;
      CG: string;
      T_CNT: number;
      T_AREA: number;
      MR: number;
      SFI: number;
      TRID: number;
    } | null;
  }[];
}

const EmisionClasificacion: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<EmisionClasificacionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth0();
  const id_empresa = user?.id_empresa;

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchEmisionData = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();

        const response = await axios.get<EmisionClasificacionData>(
          `${process.env.REACT_APP_CLASIFICACION_URL}/emision_clasificacion/${id_empresa}/${id}/`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        setData(response.data);
      } catch (error) {
        console.error('Error al cargar los datos:', error);
        setError('Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };

    if (id && id_empresa) {
      fetchEmisionData();
    }
  }, [id, id_empresa, getAccessTokenSilently]);

  const HVI_COLS = ['SCI','MST','UHML','ML','UI','STR','ELG','MIC','AMT','RD','B','CG','T_CNT','T_AREA','MR','SFI','TRID'];

  const fmtVal = (val: any, col: string): string => {
    if (col === 'CG') return String(val || '');
    if (val === null || val === undefined) return '0.00';
    return Number(val).toFixed(2);
  };

  const drawPdfHeader = (doc: jsPDF): number => {
    const pw = doc.internal.pageSize.getWidth();
    const m = 10;
    // Barra azul de título
    doc.setFillColor(24, 144, 255);
    doc.rect(m, m, pw - 2 * m, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Emisión de Clasificación — Planilla N° ${data!.nro_planilla}`, pw / 2, m + 7, { align: 'center' });
    // Cajas de info
    const infoY = m + 12;
    const boxH = 26;
    const halfW = (pw - 2 * m - 3) / 2;
    // Empresa (gris)
    doc.setFillColor(245, 245, 245);
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.rect(m, infoY, halfW, boxH, 'FD');
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(24, 144, 255);
    doc.text('Datos de Empresa', m + 3, infoY + 5);
    doc.setDrawColor(24, 144, 255);
    doc.setLineWidth(0.5);
    doc.line(m + 3, infoY + 6, m + halfW - 3, infoY + 6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50, 50, 50);
    doc.text(`Empresa: ${data!.empresa.razon}`, m + 3, infoY + 11);
    doc.text(`Dirección: ${data!.empresa.direccion}`, m + 3, infoY + 16);
    doc.text(`Localidad: ${data!.empresa.localidad}  Provincia: ${data!.empresa.provincia}`, m + 3, infoY + 21);
    // Planilla (azul claro)
    const px = m + halfW + 3;
    doc.setFillColor(230, 247, 255);
    doc.setDrawColor(145, 213, 255);
    doc.setLineWidth(0.3);
    doc.rect(px, infoY, halfW, boxH, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(24, 144, 255);
    doc.text('Datos de Planilla', px + 3, infoY + 5);
    doc.setDrawColor(24, 144, 255);
    doc.setLineWidth(0.5);
    doc.line(px + 3, infoY + 6, px + halfW - 3, infoY + 6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50, 50, 50);
    doc.text(`Fecha: ${new Date(data!.fecha).toLocaleDateString()}  HVI: ${data!.hvi || 'N/A'}  Estado: ${data!.alta === 1 ? 'ACTIVO' : 'INACTIVO'}`, px + 3, infoY + 11);
    doc.text(`Origen: ${data!.origen || 'N/A'}`, px + 3, infoY + 16);
    doc.text(`Cliente: ${data!.cliente || 'N/A'}`, px + 3, infoY + 21);
    return infoY + boxH + 5;
  };

  const addResumenTable = (doc: jsPDF, cols: string[], startY: number) => {
    const rows = cols
      .filter(c => c !== 'CG')
      .map(c => {
        const vals = data!.detalles
          .filter(d => d.alta === 1 && d.hvi)
          .map(d => Number((d.hvi as any)[c] || 0))
          .filter(v => v !== 0);
        if (!vals.length) return null;
        return [c, Math.min(...vals).toFixed(2), Math.max(...vals).toFixed(2),
          (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2)];
      })
      .filter(Boolean) as string[][];
    if (!rows.length) return;
    const pw = doc.internal.pageSize.getWidth();
    doc.setFillColor(24, 144, 255);
    doc.rect(10, startY, pw - 20, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumen de Clasificación', 13, startY + 5);
    autoTable(doc, {
      head: [['Campo', 'Mínimo', 'Máximo', 'Promedio']],
      body: rows,
      startY: startY + 8,
      tableWidth: (pw - 20) * 0.55,
      margin: { left: 10, right: 10 },
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right', fontStyle: 'bold' } },
    });
  };

  const handleGeneratePdfCompleto = async () => {
    if (!data) return;
    message.loading({ content: 'Generando PDF...', key: 'pdfGeneration' });
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const startY = drawPdfHeader(doc);
      const pw = doc.internal.pageSize.getWidth();
      doc.setFillColor(24, 144, 255);
      doc.rect(10, startY, pw - 20, 7, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Detalle de Fardos', 13, startY + 5);
      autoTable(doc, {
        head: [['Lote', 'Fardo', 'Cal.', ...HVI_COLS]],
        body: data.detalles.map(d => [
          String(d.nro_lote || '-'), String(d.nroFardo), d.calidad,
          ...HVI_COLS.map(c => fmtVal((d.hvi as any)?.[c], c)),
        ]),
        startY: startY + 8,
        margin: { left: 10, right: 10 },
        styles: { fontSize: 6.5, cellPadding: 1.5, halign: 'right' },
        headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        rowPageBreak: 'avoid',
        columnStyles: { 0: { halign: 'center', cellWidth: 10 }, 1: { halign: 'center', cellWidth: 12 }, 2: { halign: 'center', cellWidth: 14 } },
        didParseCell: (hookData: any) => {
          if (hookData.section === 'body' && data.detalles[hookData.row.index]?.alta === 0) {
            hookData.cell.styles.fillColor = [255, 235, 238];
            hookData.cell.styles.textColor = [150, 150, 150];
          }
        },
      });
      addResumenTable(doc, HVI_COLS, (doc as any).lastAutoTable.finalY + 8);
      doc.save(`clasificacion_completa_${data.nro_planilla}.pdf`);
      message.success({ content: 'PDF generado exitosamente', key: 'pdfGeneration', duration: 2 });
    } catch (err) {
      console.error('Error al generar PDF:', err);
      message.error({ content: 'Error al generar el PDF', key: 'pdfGeneration', duration: 2 });
    }
  };

  const handleGeneratePdfResumido = async () => {
    if (!data) return;
    message.loading({ content: 'Generando PDF...', key: 'pdfGeneration' });
    try {
      const colsConDatos = HVI_COLS.filter(col =>
        data.detalles.filter(d => d.alta === 1).some(d => {
          const val = (d.hvi as any)?.[col];
          return col === 'CG' ? (val !== '' && val != null) : (Number(val) !== 0);
        })
      );
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const startY = drawPdfHeader(doc);
      const pw = doc.internal.pageSize.getWidth();
      doc.setFillColor(24, 144, 255);
      doc.rect(10, startY, pw - 20, 7, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Detalle de Fardos', 13, startY + 5);
      autoTable(doc, {
        head: [['Lote', 'N° Fardo', 'Calidad', ...colsConDatos]],
        body: data.detalles.map(d => [
          String(d.nro_lote || '-'), String(d.nroFardo), d.calidad,
          ...colsConDatos.map(c => fmtVal((d.hvi as any)?.[c], c)),
        ]),
        startY: startY + 8,
        margin: { left: 10, right: 10 },
        styles: { fontSize: 10, cellPadding: 3, halign: 'right' },
        headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        rowPageBreak: 'avoid',
        columnStyles: { 0: { halign: 'center' }, 1: { halign: 'center' }, 2: { halign: 'center' } },
        didParseCell: (hookData: any) => {
          if (hookData.section === 'body' && data.detalles[hookData.row.index]?.alta === 0) {
            hookData.cell.styles.fillColor = [255, 235, 238];
            hookData.cell.styles.textColor = [150, 150, 150];
          }
        },
      });
      addResumenTable(doc, colsConDatos, (doc as any).lastAutoTable.finalY + 8);
      doc.save(`clasificacion_resumida_${data.nro_planilla}.pdf`);
      message.success({ content: 'PDF generado exitosamente', key: 'pdfGeneration', duration: 2 });
    } catch (err) {
      console.error('Error al generar PDF:', err);
      message.error({ content: 'Error al generar el PDF', key: 'pdfGeneration', duration: 2 });
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <div style={{ textAlign: 'center', color: 'red' }}>
          {error}
        </div>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <div style={{ textAlign: 'center' }}>
          No se encontraron datos para la clasificación
        </div>
      </Card>
    );
  }

  // Función para convertir calidad de texto a número
  const calidadANumero = (calidad: string): number => {
    let valor = 0;

    // Si la calidad está vacía, retornar 0
    if (!calidad) return 0;

    // Obtener la letra base (A, B, C, etc.)
    const letra = calidad.charAt(0);
    valor = (letra.charCodeAt(0) - 64) * 100; // A=100, B=200, etc.

    // Procesar fracciones
    if (calidad.includes('-1/4')) valor += 25;
    else if (calidad.includes('-1/2')) valor += 50;
    else if (calidad.includes('-3/4')) valor += 75;

    // Procesar el guión final
    if (calidad.endsWith('-')) valor += 12;

    return valor;
  };

  // Función para convertir número a calidad (la que ya teníamos)
  const convertirCalidad = (U: number): string => {
    let calidad = '';

    // Calcular U1 (letra base)
    const U1 = Math.floor(U / 100);
    if (U1 === 0) return '';
    calidad = String.fromCharCode(U1 + 64);  // Convertir número a letra (1=A, 2=B, etc.)

    // Calcular U2 (fracciones principales)
    const U2 = Math.floor(U / 25) * 25 - (U1 * 100);
    if (U2 === 25) calidad += "-1/4";
    if (U2 === 50) calidad += "-1/2";
    if (U2 === 75) calidad += "-3/4";

    // Calcular U3 (ajuste fino)
    const U3 = Math.floor(U) - (U1 * 100) - U2;
    if (U3 > 0 && U3 < 25) calidad += "-";

    return calidad;
  };

  // Modificar la función calcularResumen para usar estas funciones
  const calcularResumen = (detalles: EmisionClasificacionData['detalles']) => {
    // Filtrar detalles activos (alta = 1) para estadísticas
    const detallesActivos = detalles.filter(d => d.alta === 1);
    const detallesAnulados = detalles.filter(d => d.alta === 0);

    // Filtrar solo los detalles que tienen datos HVI válidos
    const detallesConHVI = detallesActivos.filter(d => d.hvi !== null && d.hvi !== undefined);

    // Función auxiliar para obtener valores HVI de forma segura
    const getHviValue = (detalle: any, field: string) => {
      return detalle.hvi && detalle.hvi[field] !== undefined ? detalle.hvi[field] : 0;
    };

    const resumen = {
      fardos: detallesActivos.map(d => parseInt(d.nroFardo)),
      lotes: new Set(detallesActivos.map(d => d.nro_lote)),
      calidades: new Set(detallesActivos.map(d => d.calidad)),
      calidadPromedio: {
        valor: detallesActivos.length > 0
          ? detallesActivos.reduce((sum, d) => sum + calidadANumero(d.calidad), 0) / detallesActivos.length
          : 0,
        texto: ''
      },
      hvi: {
        tipo: detallesConHVI.length > 0 && detallesConHVI[0].hvi ? detallesConHVI[0].hvi.hvi : 'N/A'
      },
      SCI: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'SCI'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'SCI'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'SCI'), 0) / detallesConHVI.length
          : 0
      },
      MST: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'MST'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'MST'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'MST'), 0) / detallesConHVI.length
          : 0
      },
      UHML: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'UHML'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'UHML'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'UHML'), 0) / detallesConHVI.length
          : 0
      },
      ML: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'ML'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'ML'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'ML'), 0) / detallesConHVI.length
          : 0
      },
      AMT: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'AMT'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'AMT'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'AMT'), 0) / detallesConHVI.length
          : 0
      },
      CG: {
        tipo: detallesConHVI.length > 0 && detallesConHVI[0].hvi ? detallesConHVI[0].hvi.CG : 'N/A'
      },
      UI: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'UI'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'UI'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'UI'), 0) / detallesConHVI.length
          : 0
      },
      RD: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'RD'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'RD'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'RD'), 0) / detallesConHVI.length
          : 0
      },
      MR: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'MR'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'MR'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'MR'), 0) / detallesConHVI.length
          : 0
      },
      ELG: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'ELG'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'ELG'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'ELG'), 0) / detallesConHVI.length
          : 0
      },
      B: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'B'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'B'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'B'), 0) / detallesConHVI.length
          : 0
      },
      STR: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'STR'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'STR'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'STR'), 0) / detallesConHVI.length
          : 0
      },
      MIC: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'MIC'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'MIC'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'MIC'), 0) / detallesConHVI.length
          : 0
      },
      T_CNT: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'T_CNT'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'T_CNT'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'T_CNT'), 0) / detallesConHVI.length
          : 0
      },
      T_AREA: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'T_AREA'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'T_AREA'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'T_AREA'), 0) / detallesConHVI.length
          : 0
      },
      SFI: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'SFI'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'SFI'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'SFI'), 0) / detallesConHVI.length
          : 0
      },
      TRID: {
        min: detallesConHVI.length > 0 ? Math.min(...detallesConHVI.map(d => getHviValue(d, 'TRID'))) : 0,
        max: detallesConHVI.length > 0 ? Math.max(...detallesConHVI.map(d => getHviValue(d, 'TRID'))) : 0,
        promedio: detallesConHVI.length > 0
          ? detallesConHVI.reduce((sum, d) => sum + getHviValue(d, 'TRID'), 0) / detallesConHVI.length
          : 0
      },
      lineasAnuladas: detallesAnulados.length
    };

    // Convertir el valor numérico promedio a texto de calidad
    resumen.calidadPromedio.texto = convertirCalidad(resumen.calidadPromedio.valor);

    return {
      ...resumen,
      fardoDesde: resumen.fardos.length > 0 ? Math.min(...resumen.fardos) : 0,
      fardoHasta: resumen.fardos.length > 0 ? Math.max(...resumen.fardos) : 0,
      loteDesde: resumen.lotes.size > 0 ? Math.min(...Array.from(resumen.lotes)) : 0,
      loteHasta: resumen.lotes.size > 0 ? Math.max(...Array.from(resumen.lotes)) : 0,
      calidadesArray: Array.from(resumen.calidades).sort()
    };
  };

  const resumen = calcularResumen(data.detalles);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '12px 20px 0' }}>
        <Button
          type="primary"
          onClick={handleGeneratePdfResumido}
          style={{ backgroundColor: theme.colors.primary, borderColor: theme.colors.primary, height: '36px', minWidth: '150px' }}
        >
          PDF Resumido
        </Button>
        <Button
          type="primary"
          onClick={handleGeneratePdfCompleto}
          style={{ backgroundColor: theme.colors.primary, borderColor: theme.colors.primary, height: '36px', minWidth: '150px' }}
        >
          PDF Completo
        </Button>
      </div>
      <div ref={contentRef}>
        <Card
          title={
            <div style={{ textAlign: 'center' }}>
              Emisión de Clasificación - Planilla N° {data?.nro_planilla}
            </div>
          }
          style={{ margin: '20px' }}
          headStyle={{
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            fontSize: '16px',
            padding: '12px'
          }}
        >
          {/* Sección de Datos de Empresa */}
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: theme.colors.backgroundGray,
            borderRadius: '6px',
            border: `1px solid ${theme.colors.border}`
          }}>
            <h3 style={{
              margin: '0 0 10px 0',
              fontSize: '16px',
              fontWeight: 'bold',
              color: theme.colors.primary,
              borderBottom: `2px solid ${theme.colors.primary}`,
              paddingBottom: '5px'
            }}>
              Datos de Empresa
            </h3>
            <div>
              <p style={{ margin: '0 0 2px 0' }}><strong>Empresa:</strong> {data?.empresa.razon}</p>
              <p style={{ margin: '0 0 2px 0' }}><strong>Dirección:</strong> {data?.empresa.direccion}</p>
              <p style={{ margin: '0 0 2px 0' }}><strong>Localidad:</strong> {data?.empresa.localidad}</p>
              <p style={{ margin: '0 0 2px 0' }}><strong>Provincia:</strong> {data?.empresa.provincia}</p>
            </div>
          </div>

          {/* Sección de Datos de Planilla */}
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: theme.colors.infoLight,
            borderRadius: '6px',
            border: `1px solid ${theme.colors.info}`
          }}>
            <h3 style={{
              margin: '0 0 10px 0',
              fontSize: '16px',
              fontWeight: 'bold',
              color: theme.colors.primary,
              borderBottom: `2px solid ${theme.colors.primary}`,
              paddingBottom: '5px'
            }}>
              Datos de Planilla
            </h3>
            <div>
              <p style={{ margin: '0 0 2px 0' }}><strong>Fecha:</strong> {data && new Date(data.fecha).toLocaleDateString()}</p>
              <p style={{ margin: '0 0 2px 0' }}><strong>Origen:</strong> {data?.origen || 'N/A'}</p>
              <p style={{ margin: '0 0 2px 0' }}><strong>Cliente:</strong> {data?.cliente || 'N/A'}</p>
              <p style={{ margin: '0 0 2px 0' }}><strong>HVI:</strong> {data?.hvi || 'N/A'}</p>
              <p style={{ margin: '0 0 2px 0' }}><strong>Observación:</strong> {data?.observaciones || 'N/A'}</p>
              <p style={{ margin: '0 0 2px 0' }}><strong>IA:</strong> {data?.IA || 'N/A'}</p>
              <p style={{ margin: '0 0 2px 0' }}>
                <strong>Estado:</strong>{' '}
                <span style={{
                  color: data?.alta === 1 ? theme.colors.success : theme.colors.error,
                  fontWeight: 'bold'
                }}>
                  {data?.alta === 1 ? 'ACTIVO' : 'INACTIVO'}
                </span>
              </p>
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: theme.colors.borderLight }}>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>Lote</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>N° Fardo</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>Calidad</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>SCI</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>MST</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>UHML</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>ML</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>UI</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>STR</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>ELG</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>MIC</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>AMT</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>RD</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>B</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>CG</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>T_CNT</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>T_AREA</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>MR</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>SFI</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>TRID</th>
              </tr>
            </thead>
            <tbody>
              {data.detalles.map((detalle, index) => (
                <tr
                  key={index}
                  style={{
                    backgroundColor: detalle.alta === 0 ? theme.colors.errorLight : (index % 2 === 0 ? '#fff' : theme.colors.backgroundGray)
                  }}
                >
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                    {detalle.nro_lote || '-'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                    {detalle.nroFardo}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                    {detalle.calidad}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.SCI?.toFixed(2) || '0.00'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.MST?.toFixed(2) || '0.00'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.UHML?.toFixed(2) || '0.00'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.ML?.toFixed(2) || '0.00'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.UI?.toFixed(2) || '0.00'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.STR?.toFixed(2) || '0.00'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.ELG?.toFixed(2) || '0.00'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.MIC?.toFixed(2) || '0.00'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.AMT?.toFixed(2) || '0.00'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.RD?.toFixed(2) || '0.00'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.B?.toFixed(2) || '0.00'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.CG || ''}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.T_CNT?.toFixed(2) || '0.00'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.T_AREA?.toFixed(2) || '0.00'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.MR?.toFixed(2) || '0.00'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.SFI?.toFixed(2) || '0.00'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {detalle.hvi?.TRID?.toFixed(2) || '0.00'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Sección de Resumen */}
          <div style={{ marginTop: '30px', borderTop: `2px solid ${theme.colors.primary}`, paddingTop: '20px' }}>
            <h3 style={{
              color: theme.colors.primary,
              marginBottom: '15px',
              textAlign: 'center',
              fontSize: '16px',
              fontWeight: 'bold'
            }}>
              Resumen de Clasificación
            </h3>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
              <tbody>
                <tr style={{ backgroundColor: theme.colors.borderLight }}>
                  <th style={{ padding: '8px', border: '1px solid #ddd', width: '20%' }}>Concepto</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd', width: '50%' }}>Rango</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd', width: '30%' }}>Promedio</th>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>Fardos</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.fardoDesde} - {resumen.fardoHasta}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>-</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>Lotes</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.loteDesde} - {resumen.loteHasta}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>-</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>Calidades</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.calidadesArray.join(', ')}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.calidadPromedio.texto}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>HVI</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.hvi.tipo || 'N/A'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.hvi.tipo || 'N/A'}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>SCI</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.SCI.min.toFixed(2)} - {resumen.SCI.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.SCI.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>MST</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.MST.min.toFixed(2)} - {resumen.MST.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.MST.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>UHML</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.UHML.min.toFixed(2)} - {resumen.UHML.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.UHML.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>ML</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.ML.min.toFixed(2)} - {resumen.ML.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.ML.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>AMT</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.AMT.min.toFixed(2)} - {resumen.AMT.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.AMT.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>CG</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.CG.tipo || 'N/A'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.CG.tipo || 'N/A'}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>UI</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.UI.min.toFixed(2)} - {resumen.UI.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.UI.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>RD</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.RD.min.toFixed(2)} - {resumen.RD.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.RD.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>MR</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.MR.min.toFixed(2)} - {resumen.MR.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.MR.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>ELG</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.ELG.min.toFixed(2)} - {resumen.ELG.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.ELG.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>B</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.B.min.toFixed(2)} - {resumen.B.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.B.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>STR</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.STR.min.toFixed(2)} - {resumen.STR.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.STR.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>MIC</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.MIC.min.toFixed(2)} - {resumen.MIC.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.MIC.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>T_CNT</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.T_CNT.min.toFixed(2)} - {resumen.T_CNT.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.T_CNT.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>T_AREA</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.T_AREA.min.toFixed(2)} - {resumen.T_AREA.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.T_AREA.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>SFI</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.SFI.min.toFixed(2)} - {resumen.SFI.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.SFI.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>TRID</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.TRID.min.toFixed(2)} - {resumen.TRID.max.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.TRID.promedio.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>Cantidad Fardos</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.fardos.length}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>-</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>Líneas anuladas</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {resumen.lineasAnuladas}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>-</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </div>

    </div>
  );
};

export default EmisionClasificacion;
